import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import {
  Button,
  ClickAwayListener,
  Grow,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Icon } from '@iconify/react';

export type GraphExpansion =
  | 'Affiliations'
  | 'Authors'
  | 'Keywords'
  | 'Impact Affiliations'
  | 'Publications'
  | 'Awards';
export interface ExpansionSelectorProps {
  onExpansionSelection: (nodeSelection: GraphExpansion) => void;
  disabled: boolean;
}

export function getExpansionIconName(graphExpansion: GraphExpansion) {
  switch (graphExpansion) {
    case 'Affiliations':
      return 'fa6-solid:building';
    case 'Authors':
      return 'ic:baseline-people-alt';
    case 'Keywords':
      return 'fa6-solid:microchip';
    case 'Impact Affiliations':
      return 'fa-solid:globe-americas';
    case 'Publications':
      return 'mdi:newspaper-variant-outline';
    case 'Awards':
      return 'mdi:trophy-award';
    default:
      return 'fa6-solid:building';
  }
}

export const ExpansionSelector: FC<ExpansionSelectorProps> = ({
  onExpansionSelection,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const [graphExpansion, setGraphExpansion] =
    useState<GraphExpansion>('Affiliations');

  const handleClose = (event: Event | SyntheticEvent) => {
    if (
      anchorRef?.current &&
      anchorRef?.current?.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleExpansionSelection = (
    event: Event | SyntheticEvent,
    graphExpansion0: GraphExpansion,
  ) => {
    setGraphExpansion(graphExpansion0);
    onExpansionSelection(graphExpansion0);
    handleClose(event);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen?.current === true && open === false) {
      anchorRef?.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const MenuChild = (props: { title: GraphExpansion }) => (
    <>
      <ListItemIcon>
        <Icon icon={getExpansionIconName(props.title)} color={'gray'} />
      </ListItemIcon>
      <>{props.title === 'Authors' ? 'People' : props.title}</>
    </>
  );

  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <>
      <Tooltip
        title={`Expand to ${
          graphExpansion === 'Authors' ? 'People' : graphExpansion
        }`}
      >
        <Button
          disabled={disabled}
          ref={anchorRef}
          variant={'outlined'}
          id="graph-expansion-composition-button"
          onClick={handleToggle}
          size={'small'}
          translate={'no'}
          sx={{
            color: '#c5cae9',
            borderColor: 'rgba(197,202,233,0.4)',
            '&:hover': { borderColor: '#6366f1', color: '#e5e7eb' },
            textTransform: 'none',
            width: 50,
            '&:disabled': {
              color: 'rgba(255,255,255,0.2)',
            },
          }}
          endIcon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        >
          <Icon
            icon={
              disabled
                ? getExpansionIconName('Affiliations')
                : getExpansionIconName(graphExpansion)
            }
            fontSize={22}
          />
        </Button>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorRef?.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        translate={'no'}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  translate={'no'}
                  id="composition-menu"
                  onKeyDown={handleListKeyDown}
                >
                  <ListItemText sx={{ textAlign: 'center', color: 'gray' }}>
                    Expand to
                  </ListItemText>
                  <MenuItem
                    onClick={(event) =>
                      handleExpansionSelection(event, 'Affiliations')
                    }
                  >
                    <MenuChild title={'Affiliations'} />
                  </MenuItem>
                  <MenuItem
                    onClick={(event) =>
                      handleExpansionSelection(event, 'Impact Affiliations')
                    }
                  >
                    <MenuChild title={'Impact Affiliations'} />
                  </MenuItem>
                  <MenuItem
                    onClick={(event) =>
                      handleExpansionSelection(event, 'Authors')
                    }
                  >
                    <MenuChild title={'Authors'} />
                  </MenuItem>
                  <MenuItem
                    onClick={(event) =>
                      handleExpansionSelection(event, 'Keywords')
                    }
                  >
                    <MenuChild title={'Keywords'} />
                  </MenuItem>
                  <MenuItem
                    onClick={(event) =>
                      handleExpansionSelection(event, 'Publications')
                    }
                  >
                    <MenuChild title={'Publications'} />
                  </MenuItem>
                  <MenuItem
                    onClick={(event) =>
                      handleExpansionSelection(event, 'Awards')
                    }
                  >
                    <MenuChild title={'Awards'} />
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
